import type {
    Plugin,
    PluginOnAnnotationLayerRender,
} from "@react-pdf-viewer/core";

export interface ReportLinkTarget {
    documentId: number;
    page: number;
}

const INT32_MAX = 2_147_483_647;
const POSITIVE_INTEGER = /^[1-9][0-9]*$/;
const CHAT_PATH = /^\/organizations\/chat\/[1-9][0-9]*$/;

function onlyValue(params: URLSearchParams, name: string): string | null {
    const values = params.getAll(name);
    return values.length === 1 ? values[0] : null;
}

/** Read an app chat link to a source page; anything else is not a report link. */
export function parseReportLink(
    href: string | null,
    origin: string
): ReportLinkTarget | null {
    if (!href) return null;
    let url: URL;
    try {
        url = new URL(href);
    } catch {
        return null;
    }
    if (url.origin !== origin || !CHAT_PATH.test(url.pathname)) return null;
    const documentId = onlyValue(url.searchParams, "open_document");
    const page = onlyValue(url.searchParams, "page");
    if (
        documentId === null ||
        page === null ||
        !POSITIVE_INTEGER.test(documentId) ||
        !POSITIVE_INTEGER.test(page) ||
        Number(documentId) > INT32_MAX ||
        !Number.isSafeInteger(Number(page))
    ) {
        return null;
    }
    return { documentId: Number(documentId), page: Number(page) };
}

function isChatLink(anchor: HTMLAnchorElement): boolean {
    try {
        return CHAT_PATH.test(new URL(anchor.href).pathname);
    } catch {
        return false;
    }
}

/**
 * Open same-origin report links from a PDF's link annotations through `onOpen`
 * on a plain primary click while `opensInApp()` allows it; any other chat link,
 * or a modifier or non-primary click on one, opens in a new browser tab.
 */
export function reportLinkPlugin(
    onOpen: (target: ReportLinkTarget) => void,
    opensInApp: () => boolean
): Plugin {
    const listeners = new Map<HTMLElement, (event: MouseEvent) => void>();

    const release = (container: HTMLElement) => {
        const listener = listeners.get(container);
        if (!listener) return;
        container.removeEventListener("click", listener);
        listeners.delete(container);
    };

    return {
        onAnnotationLayerRender({ container }: PluginOnAnnotationLayerRender) {
            for (const bound of [...listeners.keys()]) {
                if (!bound.isConnected) release(bound);
            }
            if (listeners.has(container)) return;
            const listener = (event: MouseEvent) => {
                const anchor =
                    event.target instanceof Element
                        ? event.target.closest<HTMLAnchorElement>(
                              'a[data-target="external"]'
                          )
                        : null;
                if (!anchor) return;
                const plain =
                    event.button === 0 &&
                    !event.metaKey &&
                    !event.ctrlKey &&
                    !event.shiftKey &&
                    !event.altKey;
                const target =
                    plain && opensInApp()
                        ? parseReportLink(
                              anchor.getAttribute("href"),
                              window.location.origin
                          )
                        : null;
                if (target) {
                    event.preventDefault();
                    onOpen(target);
                } else if (isChatLink(anchor)) {
                    anchor.target = "_blank";
                }
            };
            container.addEventListener("click", listener);
            listeners.set(container, listener);
        },
        uninstall() {
            for (const container of [...listeners.keys()]) release(container);
        },
    };
}
